import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Search, Plus, Edit, Trash2, Truck, Building2, User, Database, RefreshCw, Loader2, ExternalLink, CheckCircle, XCircle, Wrench, Upload, Download, Link2, Unlink, Container } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import CSVImportModal from '../../components/CSVImportModal';
import TablePagination from '../../components/TablePagination';
import GenericBulkActionsBar from '../../components/GenericBulkActionsBar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ExportFormatMenu from '../../components/common/ExportFormatMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VehiclesManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Flotte & tiers (juin 2026) : tous les rôles ops & commerciaux peuvent voir/créer/éditer/supprimer.
  // La suppression = désactivation pour les non-admin (modification), suppression réelle pour admin.
  const FLEET_ROLES = ['admin', 'lead_ops', 'ops', 'operateur', 'lead_commercial', 'commercial', 'lead_field_ops', 'field_ops'];
  const canManageFleet = FLEET_ROLES.includes(user?.role);
  const canDeleteFleet = FLEET_ROLES.includes(user?.role);
  
  // Source tab: 'local', 'external', 'all'
  const [sourceTab, setSourceTab] = useState('local');
  
  // Local vehicles state
  const [localVehicles, setLocalVehicles] = useState([]);
  const [filteredLocalVehicles, setFilteredLocalVehicles] = useState([]);
  
  // External vehicles state (from Truck Database)
  const [externalVehicles, setExternalVehicles] = useState([]);
  const [filteredExternalVehicles, setFilteredExternalVehicles] = useState([]);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalStats, setExternalStats] = useState({ total: 0, lastSync: null });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [localSourceFilter, setLocalSourceFilter] = useState('all'); // 'all', 'company', 'driver'
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Bulk selection state
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [stats, setStats] = useState({
    total: 0,
    companyFleet: 0,
    driverFleet: 0,
    disponible: 0,
    occupe: 0,
    inactif: 0
  });

  // Partner assignment state
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [partnerDialogVehicle, setPartnerDialogVehicle] = useState(null);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerResults, setPartnerResults] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(false);

  // Expiration alerts state
  const [expirationAlerts, setExpirationAlerts] = useState({ alerts: [], summary: { expired: 0, critical: 0, warning: 0, total: 0 } });
  const [showAlertDetails, setShowAlertDetails] = useState(false);

  const openPartnerDialog = (vehicle) => {
    setPartnerDialogVehicle(vehicle);
    setPartnerSearch('');
    setPartnerResults([]);
    setPartnerDialogOpen(true);
    searchPartners('');
  };

  const searchPartners = async (q) => {
    setPartnerLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (q && q.length >= 2) params.append('search', q);
      const res = await axios.get(`${API}/transport-partners/search-for-assignment?${params}`, {});
      setPartnerResults(res.data.partners || []);
    } catch { setPartnerResults([]); }
    finally { setPartnerLoading(false); }
  };

  const handleAssignPartner = async (partnerId, partnerName) => {
    if (!partnerDialogVehicle) return;
    try {
      await axios.post(`${API}/vehicles/${partnerDialogVehicle.id}/assign-partner`, {
        partnerId, partnerName
      }, {});
      toast({ title: `Vehicule rattache a ${partnerName}` });
      setPartnerDialogOpen(false);
      setPartnerDialogVehicle(null);
      fetchLocalVehicles();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const handleUnassignPartner = async (vehicleId) => {
    try {
      await axios.post(`${API}/vehicles/${vehicleId}/unassign-partner`, {}, {});
      toast({ title: 'Vehicule detache du transporteur' });
      fetchLocalVehicles();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  // Fetch local vehicles
  const fetchLocalVehicles = useCallback(async () => {
    try {
      const include_inactive = statusFilter === 'all' || statusFilter === 'inactive';
      const response = await axios.get(`${API}/vehicles/all-unified?include_inactive=${include_inactive}`);
      let data = (response.data.vehicles || []).map(v => ({ ...v, dataSource: 'local' }));
      
      if (response.data.stats) {
        setStats(response.data.stats);
      }
      
      if (statusFilter === 'inactive') {
        data = data.filter(v => v.status === 'inactif');
      }
      
      setLocalVehicles(data);
      setFilteredLocalVehicles(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching local vehicles:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les véhicules locaux",
        variant: "destructive"
      });
      setLoading(false);
    }
  }, [statusFilter]);

  // Fetch external vehicles from Truck Database
  const fetchExternalVehicles = useCallback(async (forceRefresh = false) => {
    setExternalLoading(true);
    try {
      const response = await axios.get(`${API}/external/vehicles`, {
        params: { limit: 500, force_refresh: forceRefresh },
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      
      const vehicles = (response.data.vehicles || []).map(v => ({
        id: v.id,
        registration: v.immatriculation || v.plate || v.registration,
        vehicleType: v.type_camion || v.type || v.vehicleType,
        capacity: v.capacite_tonnes || v.capacity,
        brand: v.marque || v.brand,
        model: v.modele || v.model,
        status: v.disponible !== false ? 'disponible' : 'occupe',
        dataSource: 'external',
        ownerId: v.chauffeur_id || v.proprietaire_id,
        ownerName: v.chauffeur_nom || v.proprietaire_nom
      }));
      
      setExternalVehicles(vehicles);
      setFilteredExternalVehicles(vehicles);
      setExternalStats({
        total: response.data.count || vehicles.length,
        totalCached: response.data.total_cached || vehicles.length,
        lastSync: response.data.last_sync
      });
    } catch (error) {
      console.error('Error fetching external vehicles:', error);
    } finally {
      setExternalLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocalVehicles();
    fetchExternalVehicles();
    // Fetch expiration alerts
    const fetchAlerts = async () => {
      try {
        const res = await axios.get(`${API}/vehicles/expiration-alerts?days_ahead=60`, {});
        setExpirationAlerts(res.data);
      } catch { /* alerts are optional */ }
    };
    fetchAlerts();
  }, [fetchLocalVehicles, fetchExternalVehicles]);

  // Filter vehicles based on search and local source
  useEffect(() => {
    const filterVehicles = (vehicles, isLocal = true) => {
      let filtered = vehicles;
      
      // Apply local source filter for local vehicles only
      if (isLocal && localSourceFilter !== 'all') {
        if (localSourceFilter === 'company') {
          filtered = filtered.filter(v => v.source === 'company_fleet');
        } else if (localSourceFilter === 'driver') {
          filtered = filtered.filter(v => v.source === 'driver_fleet');
        }
      }
      
      // Apply search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        filtered = filtered.filter(vehicle => {
          const reg = (vehicle.registration || vehicle.immatriculation || '').toLowerCase();
          const type = (vehicle.vehicleType || vehicle.type || '').toLowerCase();
          const brand = (vehicle.brand || vehicle.marque || '').toLowerCase();
          const model = (vehicle.model || vehicle.modele || '').toLowerCase();
          const owner = (vehicle.ownerName || '').toLowerCase();
          const partner = (vehicle.partnerName || '').toLowerCase();
          
          return reg.includes(search) || type.includes(search) || 
                 brand.includes(search) || model.includes(search) || owner.includes(search) || partner.includes(search);
        });
      }
      
      return filtered;
    };
    
    setFilteredLocalVehicles(filterVehicles(localVehicles, true));
    setFilteredExternalVehicles(filterVehicles(externalVehicles, false));
    setCurrentPage(1);
  }, [searchQuery, localVehicles, externalVehicles, localSourceFilter]);

  // Get current displayed vehicles based on source tab
  const getCurrentVehicles = () => {
    switch (sourceTab) {
      case 'local':
        return filteredLocalVehicles;
      case 'external':
        return filteredExternalVehicles;
      case 'all':
        return [...filteredLocalVehicles, ...filteredExternalVehicles];
      default:
        return filteredLocalVehicles;
    }
  };

  const currentVehicles = getCurrentVehicles();

  // Bulk selection handlers
  const handleSelectVehicle = (vehicleId) => {
    setSelectedVehicles(prev => 
      prev.includes(vehicleId) 
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const handleSelectAll = () => {
    const currentPageVehicles = currentVehicles
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      .filter(v => v.dataSource === 'local' && v.source !== 'driver_fleet');
    
    if (selectAll) {
      setSelectedVehicles(prev => prev.filter(id => !currentPageVehicles.find(v => v.id === id)));
    } else {
      const newIds = currentPageVehicles.map(v => v.id);
      setSelectedVehicles(prev => [...new Set([...prev, ...newIds])]);
    }
    setSelectAll(!selectAll);
  };

  const handleBulkDelete = async () => {
    const action = user?.role === 'admin' ? 'supprimer' : 'désactiver';
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} ${selectedVehicles.length} véhicule(s) ?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/bulk-actions/vehicles/delete`, {
        vehicleIds: selectedVehicles
      });
      toast({ title: "✅ Opération réussie", description: response.data.message });
      setSelectedVehicles([]);
      setSelectAll(false);
      fetchLocalVehicles();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'opération bulk",
        variant: "destructive"
      });
    }
  };

  const handleBulkActivate = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/vehicles/activate`, {
        vehicleIds: selectedVehicles
      });
      toast({ title: "✅ Véhicules activés", description: response.data.message });
      setSelectedVehicles([]);
      setSelectAll(false);
      fetchLocalVehicles();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'activation",
        variant: "destructive"
      });
    }
  };

  const handleBulkExport = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/vehicles/export`, {
        vehicleIds: selectedVehicles
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vehicules_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: "✅ Export réussi", description: `${selectedVehicles.length} véhicule(s) exporté(s)` });
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors de l'export", variant: "destructive" });
    }
  };

  const handleSyncExternalVehicles = async () => {
    toast({ title: "Synchronisation en cours...", description: "Récupération des véhicules depuis Truck Database" });
    await fetchExternalVehicles(true);
    toast({ title: "✅ Synchronisation terminée", description: `${externalVehicles.length} véhicules récupérés` });
  };

  useEffect(() => {
    const currentPageVehicles = currentVehicles
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      .filter(v => v.dataSource === 'local' && v.source !== 'driver_fleet');
    const allSelected = currentPageVehicles.length > 0 && 
      currentPageVehicles.every(v => selectedVehicles.includes(v.id));
    setSelectAll(allSelected);
  }, [currentPage, currentVehicles, selectedVehicles]);

  const handleDeleteVehicle = async (vehicleId, registration, dataSource, source) => {
    if (dataSource === 'external') {
      toast({
        title: "Action non disponible",
        description: "Les véhicules externes ne peuvent pas être supprimés depuis ce portail",
        variant: "destructive"
      });
      return;
    }
    
    if (source === 'driver_fleet') {
      toast({
        title: "Action non autorisée",
        description: "Les véhicules de la flotte chauffeur doivent être supprimés par le chauffeur lui-même",
        variant: "destructive"
      });
      return;
    }
    
    const action = user?.role === 'admin' ? 'supprimer' : 'désactiver';
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} le véhicule ${registration} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`);
      toast({
        title: "✅ Véhicule " + (user?.role === 'admin' ? 'supprimé' : 'désactivé'),
        description: `${registration} a été ${user?.role === 'admin' ? 'supprimé' : 'désactivé'} avec succès`
      });
      fetchLocalVehicles();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de supprimer le véhicule",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      disponible: { dotColor: 'bg-green-500', label: 'Disponible' },
      occupe: { dotColor: 'bg-amber-500', label: 'Occupé' },
      en_maintenance: { dotColor: 'bg-gray-400', label: 'Maintenance' },
      inactif: { dotColor: 'bg-gray-300', label: 'Inactif' }
    };
    const config = configs[status] || configs.disponible;
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
        <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
        {config.label}
      </span>
    );
  };

  const formatVehicleType = (type) => {
    const types = {
      plateau: 'Plateau', fourgon: 'Fourgon', fourgon_frigo: 'Fourgon Frigo',
      citerne: 'Citerne', benne: 'Benne', porte_conteneur: 'Porte-conteneur',
      semi_remorque: 'Semi-remorque', camion_plateau: 'Camion plateau', autre: 'Autre'
    };
    return types[type] || type || '-';
  };

  const getDataSourceBadge = (vehicle) => {
    if (vehicle.dataSource === 'external') {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <Database className="w-3 h-3" />
          Truck DB
        </span>
      );
    }
    if (vehicle.source === 'company_fleet') {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <Building2 className="w-3 h-3" />
          Parc
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <User className="w-3 h-3" />
        Chauffeur
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Gestion des Véhicules</h1>
          <p className="text-gray-500 mt-1">Gérer tous les véhicules du parc automobile</p>
        </div>
        <div className="flex gap-2">
          {sourceTab === 'external' && (
            <Button variant="outline" onClick={handleSyncExternalVehicles} disabled={externalLoading}>
              {externalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Synchroniser
            </Button>
          )}
          {canManageFleet && sourceTab !== 'external' && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowCSVImport(true)}
                data-testid="import-vehicles-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importer CSV
              </Button>
              <ExportFormatMenu
                endpoint={`${API}/export/vehicles`}
                filenameBase="BaobabConnect_Vehicules"
                testId="export-vehicles"
                onToast={toast}
              />
              <Button onClick={() => navigate('/admin/vehicles/new')} className="bg-[#1db584] hover:bg-[#16a075]">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau véhicule
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Source Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => { setSourceTab('local'); setCurrentPage(1); }}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            sourceTab === 'local'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Truck className="w-4 h-4" />
          Locaux
          <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{localVehicles.length}</span>
        </button>
        <button
          onClick={() => { setSourceTab('external'); setCurrentPage(1); }}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            sourceTab === 'external'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Database className="w-4 h-4" />
          Externes (Truck DB)
          <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {externalLoading ? '...' : externalVehicles.length}
          </span>
        </button>
        <button
          onClick={() => { setSourceTab('all'); setCurrentPage(1); }}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            sourceTab === 'all'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          Tous
          <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {localVehicles.length + externalVehicles.length}
          </span>
        </button>
      </div>

      {/* Stats Cards - Only for local */}
      {sourceTab === 'local' && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card><CardContent className="pt-6"><div className="text-center"><p className="text-sm text-gray-600">Total Local</p><p className="text-xl md:text-3xl font-bold text-gray-900">{stats.total}</p></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-center"><p className="text-sm text-gray-600 flex items-center justify-center gap-1"><CheckCircle className="w-4 h-4" /> Disponibles</p><p className="text-xl md:text-3xl font-bold text-gray-700">{stats.disponible}</p></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-center"><p className="text-sm text-gray-600 flex items-center justify-center gap-1"><Truck className="w-4 h-4" /> Occupés</p><p className="text-xl md:text-3xl font-bold text-gray-700">{stats.occupe}</p></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-center"><p className="text-sm text-gray-600 flex items-center justify-center gap-1"><Building2 className="w-4 h-4" /> Parc</p><p className="text-xl md:text-3xl font-bold text-gray-700">{stats.companyFleet}</p></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-center"><p className="text-sm text-gray-600 flex items-center justify-center gap-1"><User className="w-4 h-4" /> Chauffeurs</p><p className="text-xl md:text-3xl font-bold text-gray-700">{stats.driverFleet}</p></div></CardContent></Card>
        </div>
      )}

      {/* Expiration Alerts Banner */}
      {sourceTab === 'local' && (expirationAlerts.summary.expired > 0 || expirationAlerts.summary.critical > 0 || expirationAlerts.summary.warning > 0) && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4" data-testid="expiration-alerts-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900 text-sm">Alertes documents véhicules</p>
                <div className="flex items-center gap-3 mt-1 text-xs">
                  {expirationAlerts.summary.expired > 0 && (
                    <span className="text-red-700 font-semibold">{expirationAlerts.summary.expired} expire(s)</span>
                  )}
                  {expirationAlerts.summary.critical > 0 && (
                    <span className="text-orange-700 font-semibold">{expirationAlerts.summary.critical} critique(s) (&lt;7j)</span>
                  )}
                  {expirationAlerts.summary.warning > 0 && (
                    <span className="text-amber-700">{expirationAlerts.summary.warning} a surveiller (&lt;30j)</span>
                  )}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="text-orange-700 border-orange-300 hover:bg-orange-100"
              onClick={() => setShowAlertDetails(!showAlertDetails)}
              data-testid="toggle-alert-details"
            >
              {showAlertDetails ? 'Masquer' : 'Voir details'}
            </Button>
          </div>
          {showAlertDetails && expirationAlerts.alerts.length > 0 && (
            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
              {expirationAlerts.alerts.map((alert, idx) => (
                <div key={idx} className={`flex items-center justify-between text-xs p-2 rounded border ${
                  alert.status === 'expired' ? 'bg-red-50 border-red-200' :
                  alert.status === 'critical' ? 'bg-orange-50 border-orange-200' :
                  'bg-amber-50 border-amber-200'
                }`} data-testid={`alert-item-${idx}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${
                      alert.status === 'expired' ? 'bg-red-500' : alert.status === 'critical' ? 'bg-orange-500' : 'bg-amber-400'
                    }`} />
                    <span className="font-medium truncate">{alert.registration}</span>
                    <span className="text-gray-500 capitalize">{alert.documentType?.replace('_', ' ')}</span>
                    {alert.partnerName && <span className="text-gray-400">({alert.partnerName})</span>}
                  </div>
                  <span className={`font-medium shrink-0 ml-2 ${
                    alert.daysRemaining < 0 ? 'text-red-700' : alert.daysRemaining <= 7 ? 'text-orange-700' : 'text-amber-700'
                  }`}>
                    {alert.daysRemaining < 0 ? `Expire depuis ${Math.abs(alert.daysRemaining)}j` : `${alert.daysRemaining}j restants`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* External Stats Banner */}
      {sourceTab === 'external' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Véhicules Truck Database</p>
                <p className="text-sm text-blue-700">
                  {externalStats.totalCached || externalVehicles.length} véhicules en cache
                  {externalStats.lastSync && (
                    <span className="ml-2 text-blue-500">• Dernière sync: {new Date(externalStats.lastSync).toLocaleString('fr-FR')}</span>
                  )}
                </p>
              </div>
            </div>
            {externalLoading && <Loader2 className="w-5 h-5 animate-spin text-blue-600" />}
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Rechercher par immatriculation, type, marque..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {sourceTab === 'local' && (
                <div className="flex gap-2">
                  <Button variant={statusFilter === 'active' ? 'default' : 'outline'} onClick={() => setStatusFilter('active')}>Actifs</Button>
                  <Button variant={statusFilter === 'all' ? 'default' : 'outline'} onClick={() => setStatusFilter('all')}>Tous</Button>
                  <Button variant={statusFilter === 'inactive' ? 'default' : 'outline'} onClick={() => setStatusFilter('inactive')}>Inactifs</Button>
                </div>
              )}
            </div>
            
            {/* Local source filter */}
            {sourceTab === 'local' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Source locale:</span>
                <Button size="sm" variant={localSourceFilter === 'all' ? 'default' : 'outline'} onClick={() => setLocalSourceFilter('all')}>
                  Tous
                </Button>
                <Button size="sm" variant={localSourceFilter === 'company' ? 'default' : 'outline'} onClick={() => setLocalSourceFilter('company')}
                  className={localSourceFilter === 'company' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}>
                  <Building2 className="w-4 h-4 mr-1" />Parc ({stats.companyFleet})
                </Button>
                <Button size="sm" variant={localSourceFilter === 'driver' ? 'default' : 'outline'} onClick={() => setLocalSourceFilter('driver')}
                  className={localSourceFilter === 'driver' ? 'bg-purple-600 hover:bg-purple-700' : ''}>
                  <User className="w-4 h-4 mr-1" />Chauffeurs ({stats.driverFleet})
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vehicles Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Liste des Véhicules ({currentVehicles.length})
            {sourceTab === 'all' && (
              <span className="text-sm font-normal text-gray-500">
                ({filteredLocalVehicles.length} locaux + {filteredExternalVehicles.length} externes)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {sourceTab !== 'external' && (
                  <TableHead className="w-12">
                    <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} title="Sélectionner véhicules parc entreprise" />
                  </TableHead>
                )}
                <TableHead>Immatriculation</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Marque/Modèle</TableHead>
                <TableHead>Capacité</TableHead>
                <TableHead>Semi-Remorque</TableHead>
                <TableHead>Transporteur</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={sourceTab !== 'external' ? 10 : 9} className="text-center py-8 text-gray-500">
                    {externalLoading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span>Chargement des véhicules...</span>
                      </div>
                    ) : 'Aucun véhicule trouvé'}
                  </TableCell>
                </TableRow>
              ) : (
                currentVehicles
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((vehicle) => (
                  <TableRow 
                    key={`${vehicle.dataSource}-${vehicle.id}`} 
                    className={`hover:bg-gray-50 ${selectedVehicles.includes(vehicle.id) && vehicle.dataSource === 'local' ? 'bg-blue-50' : ''}`}
                  >
                    {sourceTab !== 'external' && (
                      <TableCell>
                        {vehicle.dataSource === 'local' && vehicle.source !== 'driver_fleet' ? (
                          <Checkbox 
                            checked={selectedVehicles.includes(vehicle.id)}
                            onCheckedChange={() => handleSelectVehicle(vehicle.id)}
                          />
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${
                          vehicle.dataSource === 'external' ? 'bg-blue-500' : 
                          vehicle.source === 'driver_fleet' ? 'bg-purple-500' : 'bg-[#1db584]'
                        }`}>
                          <Truck className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium font-mono">{vehicle.registration}</p>
                          {vehicle.year && <p className="text-xs text-gray-500">Année {vehicle.year}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getDataSourceBadge(vehicle)}</TableCell>
                    <TableCell>{formatVehicleType(vehicle.vehicleType)}</TableCell>
                    <TableCell>
                      {vehicle.brand ? (
                        <div>
                          <p className="font-medium">{vehicle.brand}</p>
                          {vehicle.model && <p className="text-xs text-gray-500">{vehicle.model}</p>}
                        </div>
                      ) : <span className="text-gray-400">-</span>}
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold">{vehicle.capacity || '-'} {vehicle.capacity ? 'T' : ''}</span>
                    </TableCell>
                    <TableCell data-testid={`vehicle-semi-${vehicle.id}`}>
                      {vehicle.semiTrailer?.registration ? (
                        <div className="flex items-center gap-1.5">
                          <Container className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          <div>
                            <p className="text-xs font-mono font-medium text-blue-700">{vehicle.semiTrailer.registration}</p>
                            {vehicle.semiTrailer.brand && <p className="text-[10px] text-gray-400">{vehicle.semiTrailer.brand} {vehicle.semiTrailer.bodyType ? `- ${vehicle.semiTrailer.bodyType}` : ''}</p>}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`vehicle-partner-${vehicle.id}`}>
                      {vehicle.partnerName ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full max-w-[140px] truncate" title={vehicle.partnerName}>
                          <Building2 className="w-3 h-3 shrink-0" />
                          {vehicle.partnerName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(vehicle.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {vehicle.dataSource === 'local' ? (
                          <>
                            {canManageFleet && vehicle.source !== 'driver_fleet' && (
                              vehicle.partnerId ? (
                                <Button size="sm" variant="ghost" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 w-8 p-0"
                                  onClick={() => handleUnassignPartner(vehicle.id)}
                                  title={`Detacher de ${vehicle.partnerName}`}
                                  data-testid={`unassign-partner-${vehicle.id}`}>
                                  <Unlink className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 w-8 p-0"
                                  onClick={() => openPartnerDialog(vehicle)}
                                  title="Rattacher a un transporteur"
                                  data-testid={`assign-partner-${vehicle.id}`}>
                                  <Link2 className="w-4 h-4" />
                                </Button>
                              )
                            )}
                            <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => navigate(`/admin/vehicles/${vehicle.id}`)} title="Voir details">
                              <Truck className="w-4 h-4" />
                            </Button>
                            {canManageFleet && vehicle.source !== 'driver_fleet' && (
                              <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => navigate(`/admin/vehicles/${vehicle.id}/edit`)} title="Modifier">
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {canDeleteFleet && vehicle.source !== 'driver_fleet' && (
                              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                onClick={() => handleDeleteVehicle(vehicle.id, vehicle.registration, vehicle.dataSource, vehicle.source)}
                                title={user?.role === 'admin' ? 'Supprimer' : 'Desactiver'}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        ) : (
                          <Button size="sm" variant="outline" className="text-blue-600" title="Voir dans Truck Database">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          <TablePagination
            currentPage={currentPage}
            totalItems={currentVehicles.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            itemLabel="véhicules"
          />
        </CardContent>
      </Card>
      
      {/* Bulk Actions Bar */}
      {sourceTab !== 'external' && selectedVehicles.length > 0 && (
        <GenericBulkActionsBar
          entityType="vehicles"
          selectedCount={selectedVehicles.length}
          onClearSelection={() => { setSelectedVehicles([]); setSelectAll(false); }}
          onDelete={handleBulkDelete}
          onActivate={handleBulkActivate}
          onExport={handleBulkExport}
        />
      )}

      <CSVImportModal
        open={showCSVImport}
        onOpenChange={setShowCSVImport}
        type="vehicles"
        columns={[
          { key: 'registration', label: 'Immatriculation' },
          { key: 'vehicleType', label: 'Type' },
          { key: 'brand', label: 'Marque' },
          { key: 'model', label: 'Modèle' },
          { key: 'year', label: 'Année' },
        ]}
        onSuccess={() => fetchLocalVehicles()}
      />

      {/* Partner Assignment Dialog */}
      <Dialog open={partnerDialogOpen} onOpenChange={setPartnerDialogOpen}>
        <DialogContent className="max-w-md" data-testid="partner-assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              Rattacher {partnerDialogVehicle?.registration} a un transporteur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Rechercher un transporteur..."
              value={partnerSearch}
              onChange={(e) => { setPartnerSearch(e.target.value); searchPartners(e.target.value); }}
              className="h-9"
              data-testid="partner-search-input"
              autoFocus
            />
            <div className="max-h-[360px] overflow-y-auto space-y-1">
              {partnerLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : partnerResults.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Aucun transporteur trouve</p>
              ) : (
                partnerResults.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleAssignPartner(p.id, p.name)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-transparent hover:bg-emerald-50 hover:border-emerald-200 transition-colors text-left"
                    data-testid={`partner-option-${p.id}`}
                  >
                    <div className="w-8 h-8 rounded-md bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700 shrink-0">
                      {(p.name || '?').substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                      <p className="text-xs text-gray-400">{p.type === 'Independant' ? 'Independant' : (p.type || '')} {p.phone ? `· ${p.phone}` : ''} {p.vehiclesCount > 0 ? `· ${p.vehiclesCount} vehicules` : ''}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiclesManagement;
